/**
 * Module 'launch-accounts' (ADR 0071, ADR 0078): the accounts a person saved once in Orgus for
 * each coding agent (Claude Code, Codex, Gemini CLI), chosen per session or mission, with a
 * default per project and agent. Each launch account has an agent and a kind:
 *
 *   subscription  a sign-in folder per machine, signed in once by the person in that folder; Orgus
 *                 keeps only the path, never a token. Claude Code: a config folder
 *                 (CLAUDE_CONFIG_DIR). Codex: a ChatGPT sign-in folder (CODEX_HOME, holds
 *                 auth.json). Gemini CLI: a Google sign-in folder (GEMINI_CLI_HOME, the CLI keeps
 *                 its .gemini folder inside it). The default sign-in of each agent is each
 *                 machine's own folder.
 *   api-key       the vendor's API key (Anthropic, OpenAI, Gemini), kept in the secret store (ADR 0006).
 *   foundry       Claude Code: Azure Foundry, a resource or base URL, and an optional API key.
 *   bedrock       Claude Code: Amazon Bedrock, a region and profile, and an optional bearer token.
 *   vertex        Claude Code or Gemini CLI: Google Vertex AI, a project and region (credentials
 *                 from the machine).
 *
 * The hub sends a launch's account in its grant (LaunchGrant.account) as structured fields; the
 * agent maps them to the CLI's variables (launchAccountEnv) after removing the account and
 * provider variables the session would otherwise inherit (launchAccountClearedEnv). Variable
 * names checked against code.claude.com on 19 Oct 2026, and against the Codex and Gemini CLI docs
 * and the installed CLIs on 20 Oct 2026 (ADR 0078). Browser-safe, no dependencies.
 */
import type { Id, Ts } from '../index.ts';
import type { AccountBilling } from './accounts.ts';
import { formatUsd } from './costs.ts';

export const LAUNCH_ACCOUNTS_EXT = 'launch-accounts';

export type LaunchAccountKind = 'subscription' | 'api-key' | 'foundry' | 'bedrock' | 'vertex';
export const LAUNCH_ACCOUNT_KINDS: readonly LaunchAccountKind[] = ['subscription', 'api-key', 'foundry', 'bedrock', 'vertex'];

/** Claude Code's kinds by name (ADR 0071). Other agents: launchAccountKindLabel. */
export const LAUNCH_ACCOUNT_KIND_LABEL: Record<LaunchAccountKind, string> = {
  subscription: 'Claude subscription',
  'api-key': 'Anthropic API key',
  foundry: 'Azure Foundry',
  bedrock: 'Amazon Bedrock',
  vertex: 'Google Vertex AI',
};

// ── Agents (ADR 0078) ───────────────────────────────────────────────────────

/** The coding agent an account belongs to. A record without one is Claude Code's (ADR 0071 data). */
export type LaunchAccountAgent = 'claude-code' | 'codex' | 'gemini-cli';
export const LAUNCH_ACCOUNT_AGENTS: readonly LaunchAccountAgent[] = ['claude-code', 'codex', 'gemini-cli'];
export const DEFAULT_LAUNCH_ACCOUNT_AGENT: LaunchAccountAgent = 'claude-code';

export const LAUNCH_ACCOUNT_AGENT_LABEL: Record<LaunchAccountAgent, string> = {
  'claude-code': 'Claude Code',
  codex: 'Codex',
  'gemini-cli': 'Gemini CLI',
};

/** The kinds each agent takes, in the order Add account offers them. */
export const LAUNCH_ACCOUNT_AGENT_KINDS: Record<LaunchAccountAgent, readonly LaunchAccountKind[]> = {
  'claude-code': ['subscription', 'api-key', 'foundry', 'bedrock', 'vertex'],
  codex: ['subscription', 'api-key'],
  'gemini-cli': ['subscription', 'api-key', 'vertex'],
};

const KIND_LABELS: Record<LaunchAccountAgent, Partial<Record<LaunchAccountKind, string>>> = {
  'claude-code': LAUNCH_ACCOUNT_KIND_LABEL,
  codex: { subscription: 'ChatGPT sign-in', 'api-key': 'OpenAI API key' },
  'gemini-cli': { subscription: 'Google sign-in', 'api-key': 'Gemini API key', vertex: 'Google Vertex AI' },
};

/** What a kind is called for an agent: 'ChatGPT sign-in', 'Gemini API key', 'Claude subscription'. */
export function launchAccountKindLabel(agent: LaunchAccountAgent | null | undefined, kind: LaunchAccountKind): string {
  return KIND_LABELS[agent ?? DEFAULT_LAUNCH_ACCOUNT_AGENT][kind] ?? LAUNCH_ACCOUNT_KIND_LABEL[kind];
}

/** Whether an agent takes a kind. */
export function isLaunchAccountKindFor(agent: LaunchAccountAgent, kind: unknown): kind is LaunchAccountKind {
  return LAUNCH_ACCOUNT_AGENT_KINDS[agent].includes(kind as LaunchAccountKind);
}

/** An account's agent: its own, else Claude Code (records saved before ADR 0078). */
export function launchAccountAgent(a: { agent?: unknown } | null | undefined): LaunchAccountAgent {
  return LAUNCH_ACCOUNT_AGENTS.includes(a?.agent as LaunchAccountAgent) ? (a!.agent as LaunchAccountAgent) : DEFAULT_LAUNCH_ACCOUNT_AGENT;
}

/**
 * The account agent a session's agent kind runs on: Codex for 'codex' and 'codex-acp' (the ACP
 * bridge reads the same CODEX_HOME), Gemini CLI for 'gemini-cli', Claude Code for 'claude-code' or
 * none. Null for every other agent: it takes no launch account.
 */
export function launchAccountAgentOf(agentKind: string | null | undefined): LaunchAccountAgent | null {
  const k = agentKind || 'claude-code';
  if (k === 'claude-code') return 'claude-code';
  if (k === 'codex' || k === 'codex-acp') return 'codex';
  if (k === 'gemini-cli') return 'gemini-cli';
  return null;
}

/** The session agent kind a mission starts for an account's agent when nothing else picked one. */
export function agentKindForLaunchAccount(agent: LaunchAccountAgent): string {
  return agent;
}

/**
 * Billed per token (an API key or a cloud provider), so a failover onto it never rotates between
 * consumer subscriptions (ADR 0071 decision 9, ADR 0078). Every kind but a sign-in folder.
 */
export function isPerTokenLaunchAccount(a: { kind: LaunchAccountKind }): boolean {
  return a.kind !== 'subscription';
}

/** The built-in account: each machine's own Claude config folder, found automatically. */
export const DEFAULT_LAUNCH_ACCOUNT_ID = 'default';
export const DEFAULT_LAUNCH_ACCOUNT_NAME = 'Default sign-in';

/** The built-in default sign-in of each agent: 'default' (Claude Code), 'default-codex', 'default-gemini-cli'. */
export function defaultLaunchAccountId(agent: LaunchAccountAgent): string {
  return agent === 'claude-code' ? DEFAULT_LAUNCH_ACCOUNT_ID : `${DEFAULT_LAUNCH_ACCOUNT_ID}-${agent}`;
}

/** The agent whose built-in default sign-in this id is, or null. */
export function defaultLaunchAccountAgent(id: string | null | undefined): LaunchAccountAgent | null {
  return LAUNCH_ACCOUNT_AGENTS.find((a) => defaultLaunchAccountId(a) === id) ?? null;
}

export const LAUNCH_ACCOUNT_NAME_MAX = 60;
export const LAUNCH_ACCOUNTS_MAX = 50;
export const LAUNCH_FOLDER_PATH_MAX = 1024;

/** Non-secret settings, by kind. */
export interface LaunchAccountSettings {
  /** foundry: the resource name alone (ANTHROPIC_FOUNDRY_RESOURCE). */
  resource?: string;
  /** foundry: the full base URL instead of a resource (ANTHROPIC_FOUNDRY_BASE_URL). */
  baseUrl?: string;
  /** bedrock: AWS_REGION. vertex: CLOUD_ML_REGION. */
  region?: string;
  /** bedrock: AWS_PROFILE. */
  profile?: string;
  /** vertex: ANTHROPIC_VERTEX_PROJECT_ID. */
  projectId?: string;
}

/** What the console sees of a stored secret: never its value. */
export interface LaunchAccountSecret {
  /** First 8 hex characters of the value's sha256 (ADR 0067). */
  fingerprint: string;
  setAt: Ts;
  setBy: string;
}

export interface LaunchAccount {
  id: string;
  name: string;
  /** The coding agent it is for (ADR 0078). The hub always sends it; stored records without one are Claude Code's. */
  agent: LaunchAccountAgent;
  kind: LaunchAccountKind;
  settings: LaunchAccountSettings;
  /** subscription: the config folder on each machine, by machine id. */
  folders: Record<Id, string>;
  /** api-key (required), foundry and bedrock (optional): the stored secret. */
  secret: LaunchAccountSecret | null;
  /**
   * The ADR 0067 account key its sessions are filed under (launchAccountKey). A subscription takes
   * it from its folders' sign-in; null until one reports.
   */
  accountKey: string | null;
  /**
   * A token sign-in's organisation, when the sign-in revealed it (ADR 0088 decision 7): its spend
   * key and name. Absent: the token alone is known, and its sessions are filed under
   * 'anthropic:token-<fingerprint>'. Additive.
   */
  signedInAs?: { key: string; label: string | null } | null;
  /** The default sign-in: always listed, never deleted. */
  builtIn?: boolean;
  /**
   * Per-token kinds only: the account takes over work whose subscription ran out (ADR 0071
   * decision 9, amended 20 Oct 2026). Absent or null: never used that way. Additive.
   */
  fallback?: LaunchAccountFallback | null;
  createdBy: string;
  createdAt: Ts;
  updatedBy?: string;
  updatedAt?: Ts;
}

export type LaunchFolderState = 'signed-in' | 'signed-out' | 'missing' | 'not-allowed';

export const LAUNCH_FOLDER_STATE_LABEL: Record<LaunchFolderState, string> = {
  'signed-in': 'Signed in',
  'signed-out': 'Not signed in',
  missing: 'Folder missing',
  'not-allowed': 'Not allowed on this machine',
};

/** One subscription folder on one machine, as its agent checked it (read-only). */
export interface LaunchFolderStatus {
  machineId: Id;
  accountId: string;
  path: string;
  state: LaunchFolderState;
  /** The organisation signed in (ADR 0067 key, label, billing): never an email or a person's name. */
  account: { key: string; label: string | null; billing: AccountBilling } | null;
  checkedAt: Ts;
}

/** One project's default account per agent (ADR 0078). */
export type LaunchAccountDefaults = Partial<Record<LaunchAccountAgent, string>>;

/** state.ext['launch-accounts'] */
export interface LaunchAccountsState {
  /** Each agent's default sign-in first (Claude Code, Codex, Gemini CLI), then the rest by name. */
  accounts: LaunchAccount[];
  /** The account each project's launches of each agent use when they name none; 'pool' names the agent's pool (ADR 0088). */
  defaults: Record<Id, LaunchAccountDefaults>;
  folders: LaunchFolderStatus[];
  /** Each agent's pool (ADR 0088); absent when the agent has none. */
  pools: LaunchPools;
  /** Accounts the pools skip for now, with why and until when. */
  limits: LaunchAccountLimit[];
  /** When each account last ran a launch, for the spread strategy. */
  launchedAt: Record<string, Ts>;
  /** The last Refresh usage (ADR 0088): when it started and ended (absent: never, or still running). */
  usageRefresh?: { startedAt: Ts; endedAt: Ts | null; machines: number; read: number } | null;
  /** Each account's last test (ADR 0088 decision 10), by account id. */
  tests?: Record<string, LaunchAccountTest>;
  /** ChatGPT and Google sign-ins running or just ended on machines (ADR 0108). Additive. */
  signIns?: LaunchSignIn[];
}

/**
 * Project defaults as saved before ADR 0078 (`{ [projectId]: accountId }`, Claude Code's) or now
 * (`{ [projectId]: { [agent]: accountId } }`), checked: ids only, unknown agents dropped.
 */
export function cleanLaunchDefaults(raw: unknown): Record<Id, LaunchAccountDefaults> {
  const out: Record<Id, LaunchAccountDefaults> = {};
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out;
  const okId = (v: unknown): v is string => typeof v === 'string' && /^[\w-]{1,64}$/.test(v);
  for (const [projectId, v] of Object.entries(raw as Record<string, unknown>)) {
    if (okId(v)) out[projectId] = { [DEFAULT_LAUNCH_ACCOUNT_AGENT]: v };
    else if (v && typeof v === 'object' && !Array.isArray(v)) {
      const d: LaunchAccountDefaults = {};
      for (const agent of LAUNCH_ACCOUNT_AGENTS) {
        const id = (v as Record<string, unknown>)[agent];
        if (okId(id)) d[agent] = id;
      }
      if (Object.keys(d).length) out[projectId] = d;
    }
  }
  return out;
}

/** POST /api/launch-accounts, PUT /api/launch-accounts/:id (every field optional on PUT). */
export interface LaunchAccountInput {
  /** Absent or empty on POST: the hub names it after its kind ("Claude subscription", "Claude subscription 2"). */
  name?: string;
  /** The agent it is for (absent: Claude Code). It never changes after. */
  agent?: LaunchAccountAgent;
  kind: LaunchAccountKind;
  settings?: LaunchAccountSettings;
  /** The API key, Foundry key or Bedrock token: write-only. '' on PUT removes it (where optional). */
  secret?: string;
}

/** PUT /api/launch-accounts/:id/folders/:machineId: a path on that machine, or create one under the agent's home. */
export interface LaunchFolderInput {
  path?: string;
  create?: boolean;
}

/**
 * PUT /api/launch-accounts/defaults/:projectId. An account sets its own agent's default; null
 * clears the default of `agent` (absent: Claude Code).
 */
export interface LaunchDefaultInput {
  accountId: string | null;
  agent?: LaunchAccountAgent;
}

/** LaunchGrant.account: the account one launch runs on. The secret travels only here, for the spawn. */
export interface LaunchAccountGrant {
  id: string;
  name: string;
  /** The agent it is for (absent: Claude Code, a hub before ADR 0078). */
  agent?: LaunchAccountAgent;
  kind: LaunchAccountKind;
  accountKey: string | null;
  /**
   * subscription: the folder on this machine; absent for the default sign-in (the machine's own).
   * Codex and Gemini CLI per-token accounts: absent from the hub; the agent fills in the
   * Orgus-owned folder it runs them in (launchAccountFolderDir).
   */
  configDir?: string;
  settings: LaunchAccountSettings;
  /** The leased secret, when the account has one. */
  secret?: string;
}

// ── Agent messages (module 'launch-accounts') ───────────────────────────────

/**
 * hub -> agent: the subscription folders this machine holds, on connect and on every change, with
 * the agent each is for (absent: Claude Code), so the machine checks the right sign-in.
 */
export const LAUNCH_FOLDERS_TYPE = 'folders';
export interface LaunchFoldersMessage {
  folders: { accountId: string; path: string; agent?: LaunchAccountAgent }[];
}
/** hub -> agent, with an id: create the account's folder under the agent's home. */
export const LAUNCH_FOLDER_CREATE_TYPE = 'folder.create';
/** agent -> hub, same id: { accountId, path } or { accountId, error }. */
export const LAUNCH_FOLDER_CREATED_TYPE = 'folder.created';
/**
 * agent -> hub: each folder's state (each agent's own folder with its default account id:
 * 'default', 'default-codex', 'default-gemini-cli').
 */
export const LAUNCH_FOLDERS_STATUS_TYPE = 'folders.status';
export interface LaunchFoldersStatusMessage {
  folders: Omit<LaunchFolderStatus, 'machineId'>[];
}

/** The folder Orgus creates for a Claude Code account, relative to the agent's home. */
export const LAUNCH_FOLDERS_DIR = 'claude-accounts';
/** The agent's environment variable a machine owner lists extra allowed Claude Code folders in. */
export const LAUNCH_FOLDERS_ENV = 'ORGUS_CLAUDE_CONFIG_DIRS';

/** Per agent: the folder Orgus creates accounts in under the agent's home (ADR 0078). */
export const LAUNCH_ACCOUNT_FOLDERS_DIR: Record<LaunchAccountAgent, string> = {
  'claude-code': LAUNCH_FOLDERS_DIR,
  codex: 'codex-accounts',
  'gemini-cli': 'gemini-accounts',
};

/** Per agent: the variable a machine owner lists extra allowed folders in (path-delimiter separated). */
export const LAUNCH_ACCOUNT_FOLDERS_ENV: Record<LaunchAccountAgent, string> = {
  'claude-code': LAUNCH_FOLDERS_ENV,
  codex: 'ORGUS_CODEX_HOMES',
  'gemini-cli': 'ORGUS_GEMINI_HOMES',
};

/** Per agent: the variable that points the CLI at a folder (the subscription folder). */
export const LAUNCH_ACCOUNT_FOLDER_VAR: Record<LaunchAccountAgent, string> = {
  'claude-code': 'CLAUDE_CONFIG_DIR',
  codex: 'CODEX_HOME',
  'gemini-cli': 'GEMINI_CLI_HOME',
};

// ── Environment ─────────────────────────────────────────────────────────────

/**
 * Account and provider variables a launch on a chosen account removes from what the session
 * would inherit, before it sets its own: the chosen account wins over the machine's.
 */
export const LAUNCH_ACCOUNT_CLEARED_ENV: readonly string[] = [
  'CLAUDE_CONFIG_DIR',
  'ANTHROPIC_API_KEY',
  'ANTHROPIC_AUTH_TOKEN',
  'ANTHROPIC_BASE_URL',
  'CLAUDE_CODE_OAUTH_TOKEN',
  'CLAUDE_CODE_USE_FOUNDRY',
  'CLAUDE_CODE_USE_BEDROCK',
  'CLAUDE_CODE_USE_VERTEX',
  'CLAUDE_CODE_USE_MANTLE',
  'ANTHROPIC_FOUNDRY_RESOURCE',
  'ANTHROPIC_FOUNDRY_BASE_URL',
  'ANTHROPIC_FOUNDRY_API_KEY',
  'ANTHROPIC_FOUNDRY_AUTH_TOKEN',
  'ANTHROPIC_VERTEX_PROJECT_ID',
  'AWS_BEARER_TOKEN_BEDROCK',
];

/**
 * Codex (ADR 0078): the folder, its API key variables (CODEX_API_KEY wins over every other sign-in
 * for codex exec and the codex-acp bridge; OPENAI_API_KEY is the bridge's other one), the base URL
 * and the organisation and project an OpenAI key could be pinned to.
 */
export const CODEX_ACCOUNT_CLEARED_ENV: readonly string[] = ['CODEX_HOME', 'CODEX_API_KEY', 'OPENAI_API_KEY', 'OPENAI_BASE_URL', 'OPENAI_ORGANIZATION', 'OPENAI_PROJECT'];

/**
 * Gemini CLI (ADR 0078): the folder, its keys, and every switch its getAuthTypeFromEnv reads
 * (Google sign-in, Vertex AI, a gateway, Compute Engine credentials) with their project and region.
 * GOOGLE_APPLICATION_CREDENTIALS stays: it is the machine's own Google credentials, which Vertex uses.
 */
export const GEMINI_ACCOUNT_CLEARED_ENV: readonly string[] = [
  'GEMINI_CLI_HOME',
  'GEMINI_API_KEY',
  'GOOGLE_API_KEY',
  'GOOGLE_GENAI_USE_VERTEXAI',
  'GOOGLE_GENAI_USE_GCA',
  'GOOGLE_CLOUD_PROJECT',
  'GOOGLE_CLOUD_PROJECT_ID',
  'GOOGLE_CLOUD_LOCATION',
  'GOOGLE_GEMINI_BASE_URL',
  'GEMINI_CLI_USE_COMPUTE_ADC',
];

/** The variables a launch on a chosen account of this agent removes first. */
export function launchAccountClearedEnv(agent: LaunchAccountAgent | null | undefined): readonly string[] {
  if (agent === 'codex') return CODEX_ACCOUNT_CLEARED_ENV;
  if (agent === 'gemini-cli') return GEMINI_ACCOUNT_CLEARED_ENV;
  return LAUNCH_ACCOUNT_CLEARED_ENV;
}

/** True when a launch on a chosen account removes this variable (compared in upper case). Claude Code's list by default. */
export function isLaunchAccountEnvName(name: string, agent?: LaunchAccountAgent | null): boolean {
  return launchAccountClearedEnv(agent).includes(name.toUpperCase());
}

/**
 * Gemini CLI in ACP mode takes its auth type only from its settings (security.auth.selectedType),
 * never from the environment, so a key or Vertex account runs in an Orgus-owned folder whose
 * settings.json names it (the key itself stays in the environment). Null: the kind needs no folder.
 */
export function geminiSelectedAuthType(kind: LaunchAccountKind): 'oauth-personal' | 'gemini-api-key' | 'vertex-ai' | null {
  if (kind === 'api-key') return 'gemini-api-key';
  if (kind === 'vertex') return 'vertex-ai';
  return null;
}

/**
 * Whether the agent runs this account in a folder it owns and fills in itself (configDir is absent
 * from the grant): Gemini CLI keys and Vertex (geminiSelectedAuthType).
 */
export function launchAccountOwnFolder(g: { agent?: LaunchAccountAgent | null; kind: LaunchAccountKind }): boolean {
  return g.agent === 'gemini-cli' && geminiSelectedAuthType(g.kind) !== null;
}

/**
 * The variables one account sets for its agent's CLI. Nothing for a default sign-in (no folder).
 * Gemini CLI keys and Vertex also need the folder the agent made (configDir, launchAccountOwnFolder).
 */
export function launchAccountEnv(g: Pick<LaunchAccountGrant, 'kind' | 'configDir' | 'settings' | 'secret'> & { agent?: LaunchAccountAgent | null }): Record<string, string> {
  const s = g.settings ?? {};
  const env: Record<string, string> = {};
  const put = (k: string, v: string | undefined) => {
    if (typeof v === 'string' && v.trim()) env[k] = v.trim();
  };
  if (g.agent === 'codex') {
    if (g.kind === 'subscription') put('CODEX_HOME', g.configDir);
    else if (g.kind === 'api-key') put('CODEX_API_KEY', g.secret);
    return env;
  }
  if (g.agent === 'gemini-cli') {
    put('GEMINI_CLI_HOME', g.configDir);
    if (g.kind === 'api-key') put('GEMINI_API_KEY', g.secret);
    else if (g.kind === 'vertex') {
      env.GOOGLE_GENAI_USE_VERTEXAI = 'true';
      put('GOOGLE_CLOUD_PROJECT', s.projectId);
      put('GOOGLE_CLOUD_LOCATION', s.region);
    }
    return env;
  }
  switch (g.kind) {
    case 'subscription':
      put('CLAUDE_CONFIG_DIR', g.configDir);
      // The hub-held sign-in token (ADR 0088 amendment) outranks whatever folder the session runs in.
      put('CLAUDE_CODE_OAUTH_TOKEN', g.secret);
      break;
    case 'api-key':
      put('ANTHROPIC_API_KEY', g.secret);
      break;
    case 'foundry':
      env.CLAUDE_CODE_USE_FOUNDRY = '1';
      if (s.resource) put('ANTHROPIC_FOUNDRY_RESOURCE', s.resource);
      else put('ANTHROPIC_FOUNDRY_BASE_URL', s.baseUrl);
      put('ANTHROPIC_FOUNDRY_API_KEY', g.secret);
      break;
    case 'bedrock':
      env.CLAUDE_CODE_USE_BEDROCK = '1';
      put('AWS_REGION', s.region);
      put('AWS_PROFILE', s.profile);
      put('AWS_BEARER_TOKEN_BEDROCK', g.secret);
      break;
    case 'vertex':
      env.CLAUDE_CODE_USE_VERTEX = '1';
      put('CLOUD_ML_REGION', s.region);
      put('ANTHROPIC_VERTEX_PROJECT_ID', s.projectId);
      break;
  }
  return env;
}

/**
 * Whether a kind needs, may have, or never has a secret. A Claude subscription may hold the
 * long-lived sign-in token `claude setup-token` issues (ADR 0088 amendment): then it runs on every
 * machine with no folder, as CLAUDE_CODE_OAUTH_TOKEN. Other agents' sign-ins take none.
 */
export function launchAccountSecretNeed(kind: LaunchAccountKind, agent: LaunchAccountAgent = DEFAULT_LAUNCH_ACCOUNT_AGENT): 'required' | 'optional' | 'none' {
  if (kind === 'api-key') return 'required';
  if (kind === 'foundry' || kind === 'bedrock') return 'optional';
  if (kind === 'subscription' && agent === 'claude-code') return 'optional';
  return 'none';
}

/** What the secret is called in the console, by kind. */
export const LAUNCH_ACCOUNT_SECRET_LABEL: Record<LaunchAccountKind, string> = {
  subscription: 'Sign-in token',
  'api-key': 'API key',
  foundry: 'Foundry API key',
  bedrock: 'Bedrock API key',
  vertex: '',
};

/** A safe id part, as the agent's accountFromEnv cuts it: letters, digits, dot, dash, underscore; at most 60. */
function ident(v: string | undefined): string | null {
  const s = (v ?? '').trim().replace(/[^A-Za-z0-9._-]/g, '').slice(0, 60);
  return s || null;
}

/** The first label of a URL's host ('contoso' of https://contoso.services.ai.azure.com/anthropic). No URL global: browser-safe and lib-free. */
function hostLabel(url: string | undefined): string | null {
  const m = url ? /^[a-z][a-z0-9+.-]*:\/\/(?:[^@/?#]*@)?([^/:?#]+)/i.exec(url.trim()) : null;
  return m ? ident(m[1]!.split('.')[0]) : null;
}

/**
 * The ADR 0067 account key a launch account's sessions are filed under, the same key the agent's
 * accountFromEnv computes from the launch environment. `orgKey`: a subscription's signed-in
 * organisation ('anthropic:<uuid>'), from its folder status.
 */
export function launchAccountKey(a: { agent?: LaunchAccountAgent | null; kind: LaunchAccountKind; settings?: LaunchAccountSettings; fingerprint?: string | null; orgKey?: string | null; tokenOrgKey?: string | null }): string | null {
  const s = a.settings ?? {};
  // ADR 0078: an OpenAI key 'openai:<8 hex>', a Gemini key 'gemini:<8 hex>'; a ChatGPT or Google
  // sign-in takes its folder's key ('chatgpt:<8 hex>', 'google:<8 hex>'); Vertex as for Claude Code.
  if (a.agent === 'codex' && a.kind === 'api-key') return a.fingerprint ? `openai:${a.fingerprint}` : null;
  if (a.agent === 'gemini-cli' && a.kind === 'api-key') return a.fingerprint ? `gemini:${a.fingerprint}` : null;
  switch (a.kind) {
    case 'subscription':
      // A Claude token sign-in (ADR 0088 decision 7) runs on the token, not on a folder's sign-in:
      // its organisation when the sign-in said, else a key of its own.
      if (a.fingerprint && (a.agent ?? DEFAULT_LAUNCH_ACCOUNT_AGENT) === 'claude-code') return a.tokenOrgKey ?? `anthropic:token-${a.fingerprint}`;
      return a.orgKey ?? null;
    case 'api-key':
      return a.fingerprint ? `api-key:${a.fingerprint}` : null;
    case 'foundry': {
      const r = ident(s.resource) ?? hostLabel(s.baseUrl);
      return `foundry:${r?.toLowerCase() ?? 'default'}`;
    }
    case 'bedrock': {
      const region = ident(s.region);
      const profile = ident(s.profile);
      return !region && !profile ? 'bedrock:default' : `bedrock:${profile ?? 'default'}@${region ?? 'default'}`;
    }
    case 'vertex':
      return `vertex:${ident(s.projectId) ?? 'default'}`;
  }
}

// ── Validation ─────────────────────────────────────────────────────────────

const RESOURCE = /^[A-Za-z0-9][A-Za-z0-9-]{0,62}$/;
const REGION = /^[a-z0-9-]{2,40}$/;
const PROFILE = /^[A-Za-z0-9._@+=,-]{1,64}$/;
const GCP_PROJECT = /^[a-z][a-z0-9-]{4,61}[a-z0-9]$/;

/** One line, at most LAUNCH_ACCOUNT_NAME_MAX characters. */
export function cleanLaunchAccountName(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const one = v.replace(/\s+/g, ' ').trim();
  return one && one.length <= LAUNCH_ACCOUNT_NAME_MAX ? one : null;
}

/**
 * The settings a kind takes, checked; `error` names what is wrong in words a person can act on.
 * Unknown keys and keys of another kind are dropped.
 */
export function cleanLaunchAccountSettings(kind: LaunchAccountKind, raw: unknown, agent: LaunchAccountAgent = DEFAULT_LAUNCH_ACCOUNT_AGENT): { settings: LaunchAccountSettings; error?: string } {
  const r = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const str = (k: string) => (typeof r[k] === 'string' ? (r[k] as string).trim() : '');
  const out: LaunchAccountSettings = {};
  if (kind === 'foundry') {
    const resource = str('resource');
    const baseUrl = str('baseUrl');
    if (resource && !RESOURCE.test(resource)) return { settings: out, error: 'The Foundry resource is its name alone, such as my-resource: letters, digits and dashes, not a URL.' };
    if (baseUrl) {
      const ok = /^https:\/\/[A-Za-z0-9.-]+(:\d{1,5})?(\/[^\s"]*)?$/.test(baseUrl);
      if (!ok) return { settings: out, error: 'The Foundry base URL must be an https address, such as https://my-resource.services.ai.azure.com/anthropic.' };
    }
    if (!resource && !baseUrl) return { settings: out, error: 'A Foundry account needs its resource name or its base URL.' };
    if (resource) out.resource = resource;
    else out.baseUrl = baseUrl;
  } else if (kind === 'bedrock') {
    const region = str('region');
    const profile = str('profile');
    if (region && !REGION.test(region)) return { settings: out, error: 'The AWS region looks like us-east-1.' };
    if (profile && !PROFILE.test(profile)) return { settings: out, error: 'The AWS profile is the name of a profile in ~/.aws, without spaces.' };
    if (region) out.region = region;
    if (profile) out.profile = profile;
  } else if (kind === 'vertex') {
    const projectId = str('projectId');
    const region = str('region');
    if (!projectId || !GCP_PROJECT.test(projectId)) return { settings: out, error: 'A Vertex account needs its Google Cloud project id: 6 to 30 lowercase letters, digits and dashes.' };
    if (region && !REGION.test(region)) return { settings: out, error: 'The Vertex region is global, us, eu or a region such as us-east5.' };
    // Gemini CLI on Vertex needs its location too (GOOGLE_CLOUD_LOCATION).
    if (!region && agent === 'gemini-cli') return { settings: out, error: 'A Gemini Vertex account needs its location, such as us-central1 or global.' };
    out.projectId = projectId;
    if (region) out.region = region;
  }
  return { settings: out };
}

/** The settings line a row shows: "my-resource", "us-east-1, profile dev", "project acme-ai, global". */
export function launchAccountSummary(a: Pick<LaunchAccount, 'kind' | 'settings'>): string {
  const s = a.settings ?? {};
  switch (a.kind) {
    case 'foundry':
      return s.resource ?? hostLabel(s.baseUrl) ?? '';
    case 'bedrock':
      return [s.region, s.profile ? `profile ${s.profile}` : ''].filter(Boolean).join(', ');
    case 'vertex':
      return [s.projectId ? `project ${s.projectId}` : '', s.region ?? ''].filter(Boolean).join(', ');
    default:
      return '';
  }
}

/** A folder path as a person typed it: absolute (POSIX, a drive letter or a UNC path), one line. */
export function isLaunchFolderPath(p: unknown): p is string {
  if (typeof p !== 'string' || !p || p.length > LAUNCH_FOLDER_PATH_MAX || /[\u0000-\u001f"]/.test(p)) return false;
  return p.startsWith('/') || /^[A-Za-z]:[\\/]/.test(p) || p.startsWith('\\\\');
}

/**
 * Claude Code's sign-in, after its own command: `claude auth login` signs the config folder in
 * with a Claude subscription (the default of `--claudeai`) and exits. Checked against Claude
 * Code 2.1.304 (`claude auth login --help`) on 20 Oct 2026.
 */
export const LAUNCH_SIGN_IN_ARGS: readonly string[] = ['auth', 'login'];

/**
 * `claude setup-token`: the OAuth sign-in in the browser, then a long-lived token printed in the
 * terminal (requires a Claude subscription; checked against Claude Code 2.1.304 on 21 Oct 2026).
 * The console's sign-in terminal runs it in a scratch folder, captures the token, and the hub
 * keeps it as the account's secret (ADR 0088 amendment).
 */
export const LAUNCH_SETUP_TOKEN_ARGS: readonly string[] = ['setup-token'];

/** A Claude OAuth token as setup-token prints it: 'sk-ant-oat01-' then the body. */
const CLAUDE_OAUTH_TOKEN_RE = /sk-ant-oat\d{2}-[A-Za-z0-9_-]{40,}/g;

/** The first Claude OAuth token in a piece of terminal output, or null. */
export function findClaudeOauthToken(text: string): string | null {
  const m = new RegExp(CLAUDE_OAUTH_TOKEN_RE.source).exec(text);
  return m ? m[0] : null;
}

/** Terminal output with every Claude OAuth token replaced, so the browser never shows one. */
export function maskClaudeOauthTokens(text: string, mask = '<sign-in token, saved by Orgus>'): string {
  return text.replace(CLAUDE_OAUTH_TOKEN_RE, mask);
}

/** True when the end of a chunk could be the start of a token split across chunks. */
export function endsInTokenPrefix(text: string): boolean {
  return /sk-ant-oat?\d{0,2}-?[A-Za-z0-9_-]{0,39}$/.test(text) || /sk-ant-oa?t?$/.test(text) || /sk-?a?n?t?-?$/.test(text.slice(-7));
}

/**
 * agent -> hub: a sign-in terminal captured the account's token: { accountId, token, account? }.
 * Travels once, to the secret store. `account`: the organisation the sign-in revealed, if any
 * (accountFromOauth of the scratch folder's .claude.json; never an email or a person's name).
 */
export const LAUNCH_TOKEN_CAPTURED_TYPE = 'token.captured';
/** agent -> hub: the organisation a token sign-in revealed, found after the token: { accountId, account }. */
export const LAUNCH_TOKEN_IDENTITY_TYPE = 'token.identity';

/** Whether a name is the one the hub gave an account that was added without one ("Claude subscription 2"). */
export function isDefaultLaunchAccountName(name: string, agent: LaunchAccountAgent, kind: LaunchAccountKind): boolean {
  const base = launchAccountKindLabel(agent, kind);
  return name === base || (name.startsWith(`${base} `) && /^\d+$/.test(name.slice(base.length + 1)));
}

/**
 * The one command that signs a person in to Claude Code in a folder, for the machine's shell:
 * PowerShell on Windows, a POSIX shell elsewhere. The console runs the same in a sign-in
 * terminal (module term, mode 'sign-in'); this is the fallback for one's own terminal.
 */
export function signInCommand(folder: string, os: 'windows' | 'macos' | 'linux' | string, agent: LaunchAccountAgent = DEFAULT_LAUNCH_ACCOUNT_AGENT): string {
  const name = LAUNCH_ACCOUNT_FOLDER_VAR[agent];
  // Codex: `codex login` opens the ChatGPT browser sign-in. Gemini CLI has no login command: it
  // asks how to sign in on its first start (choose Sign in with Google), or /auth inside it.
  const cmd = agent === 'codex' ? 'codex login' : agent === 'gemini-cli' ? 'gemini' : `claude ${LAUNCH_SIGN_IN_ARGS.join(' ')}`;
  if (os === 'windows') return `$env:${name} = '${folder.replace(/'/g, "''")}'; ${cmd}`;
  return `${name}='${folder.replace(/'/g, `'\\''`)}' ${cmd}`;
}

/**
 * The account a launch uses: the one it names, else the project's default, else none (today's
 * behaviour). Either may be POOL_LAUNCH_ACCOUNT_ID (ADR 0088): the hub then picks from the pool.
 */
export function chosenLaunchAccount(
  named: string | null | undefined,
  projectId: Id | null | undefined,
  defaults: Record<Id, LaunchAccountDefaults>,
  agent: LaunchAccountAgent = DEFAULT_LAUNCH_ACCOUNT_AGENT,
): { id: string; from: 'named' | 'project' } | null {
  if (named) return { id: named, from: 'named' };
  const d = projectId ? defaults[projectId]?.[agent] : undefined;
  return d ? { id: d, from: 'project' } : null;
}

// ── Failover onto a per-token account (ADR 0071 decision 9, amended 20 Oct 2026) ─

/**
 * How an account a person marked "Use when a subscription runs out" takes over. Only a per-token
 * kind may carry it, never a subscription and never the default sign-in: Orgus never moves work
 * from one subscription to another.
 */
export interface LaunchAccountFallback {
  /** The projects it serves; null: every project. */
  projects: Id[] | null;
  /** Its place among the fallback accounts, 1 first; ties by name. */
  order: number;
  /**
   * USD a UTC day this account may spend before Orgus stops moving work onto it, as Spend
   * measures its account key (ADR 0067). Checked before each start; null: no cap.
   */
  dailyCapUsd: number | null;
  setBy: string;
  setAt: Ts;
}

/** PUT /api/launch-accounts/:id/fallback. `on: false` removes it; the rest is kept or set when on. */
export interface LaunchFallbackInput {
  on: boolean;
  projects?: Id[] | null;
  order?: number;
  dailyCapUsd?: number | null;
}

export const FALLBACK_CAP_MAX_USD = 100_000;
export const FALLBACK_PROJECTS_MAX = 200;

/** Why a fallback change cannot be made, in words a person can act on, or null. */
export function launchFallbackError(a: Pick<LaunchAccount, 'kind'>, input: unknown): string | null {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return 'Send { on, projects, order, dailyCapUsd }.';
  const b = input as Record<string, unknown>;
  if (typeof b.on !== 'boolean') return 'Say whether the account takes over: on is true or false.';
  if (!b.on) return null;
  if (!isPerTokenLaunchAccount(a)) return 'Only an account billed per token can take over when a subscription runs out. Orgus never moves work to another subscription.';
  if (b.projects !== undefined && b.projects !== null) {
    if (!Array.isArray(b.projects) || !b.projects.length) return 'Choose at least one project, or every project.';
    if (b.projects.length > FALLBACK_PROJECTS_MAX) return `Choose at most ${FALLBACK_PROJECTS_MAX} projects, or every project.`;
    if (b.projects.some((p) => typeof p !== 'string' || !/^[\w.:@-]{1,128}$/.test(p))) return 'A project in the list is not valid.';
  }
  if (b.order !== undefined && (typeof b.order !== 'number' || !Number.isInteger(b.order) || b.order < 1 || b.order > 50)) return 'The order is a whole number from 1.';
  if (b.dailyCapUsd !== undefined && b.dailyCapUsd !== null && (typeof b.dailyCapUsd !== 'number' || !Number.isFinite(b.dailyCapUsd) || b.dailyCapUsd <= 0 || b.dailyCapUsd > FALLBACK_CAP_MAX_USD)) {
    return `The daily cap is an amount in dollars above 0 and at most ${FALLBACK_CAP_MAX_USD}, or none.`;
  }
  return null;
}

/** Fallback accounts in the order they are tried: their order, then name. */
export function orderedFallbacks<A extends Pick<LaunchAccount, 'name' | 'fallback' | 'kind'>>(accounts: readonly A[]): A[] {
  return accounts.filter((a) => !!a.fallback && isPerTokenLaunchAccount(a)).sort((x, y) => x.fallback!.order - y.fallback!.order || x.name.localeCompare(y.name));
}

/** Why one fallback account was passed over. */
export interface FallbackSkip {
  id: string;
  name: string;
  /** 'cap': it spent its daily cap today; 'unusable': it cannot run now (no key, not allowed). */
  why: 'cap' | 'unusable';
  /** One plain sentence. */
  text: string;
}

/**
 * The account work moves to when its subscription ran out: the first fallback account of the same
 * coding agent, serving the project, not tried already, under its daily cap and usable. Never a
 * subscription. `spentToday`: USD the account spent this UTC day; `unusable`: why it cannot run
 * now, or null.
 */
export function pickFallbackAccount<A extends Pick<LaunchAccount, 'id' | 'name' | 'fallback' | 'kind'> & { agent?: unknown }>(
  accounts: readonly A[],
  o: { projectId: Id | null; agentKind: string; exclude: readonly string[]; spentToday: (a: A) => number; unusable?: (a: A) => string | null },
): { account: A | null; skipped: FallbackSkip[] } {
  const skipped: FallbackSkip[] = [];
  for (const a of orderedFallbacks(accounts)) {
    const f = a.fallback!;
    if (launchAccountAgent(a) !== launchAccountAgentOf(o.agentKind) || o.exclude.includes(a.id)) continue;
    if (f.projects && (!o.projectId || !f.projects.includes(o.projectId))) continue;
    const why = o.unusable?.(a) ?? null;
    if (why) {
      skipped.push({ id: a.id, name: a.name, why: 'unusable', text: why });
      continue;
    }
    if (f.dailyCapUsd != null && o.spentToday(a) >= f.dailyCapUsd) {
      skipped.push({ id: a.id, name: a.name, why: 'cap', text: `${a.name} reached its daily cap of ${formatUsd(f.dailyCapUsd)}.` });
      continue;
    }
    return { account: a, skipped };
  }
  return { account: null, skipped };
}

/** The line a start that moved onto a fallback account carries, for the session and the mission's Starts. */
export function accountFallbackNote(from: string, to: string): string {
  return `${from} reached its usage limit. The work continues on ${to}, billed per token.`;
}

/** An account as the scheduler sees it: enough to name it and to know how it bills. */
export interface LaunchAccountRef {
  id: string;
  name: string;
  /** Billed per use: never a subscription or the default sign-in. */
  perToken: boolean;
  /** Marked to take over when a subscription runs out. */
  fallback: boolean;
}

/** What other hub modules may ask the launch-accounts module: ctx.ext<LaunchAccountsApi>('launch-accounts'). */
export interface LaunchAccountsApi {
  /**
   * The account a launch of this coding agent runs on: the one it names (when it serves that
   * agent), else the project's default (likewise), else the machine's own sign-in ('default').
   */
  resolve?(o: { named: string | null; projectId: Id | null; agentKind: string }): LaunchAccountRef;
  /** The fallback account work moves to now (pickFallbackAccount with today's spend), and why others were passed over. */
  fallbackFor?(o: { projectId: Id | null; agentKind: string; exclude: readonly string[]; machineId: Id }): { account: LaunchAccountRef | null; skipped: FallbackSkip[] };
  /**
   * Why a launch on this machine that names this account cannot use it, or null when it can (the
   * session.start gate). The id 'pool' asks whether any account of the agent's pool can (ADR 0088).
   */
  check(id: string, machineId?: Id, agentKind?: string | null): string | null;
  /** The agent an account is for (ADR 0078), or null when there is no such account. */
  agentOf?(id: string): LaunchAccountAgent | null;
  /**
   * The account one launch runs on (ADR 0071 decision 5): the one it names, else its project's
   * default; a resume keeps exactly the account its session ran on (named, or none). `secretId` is
   * the owned secret to lease with it. `mode` 'pty': an in-console terminal, whose environment
   * the agent sets itself, so it takes an account like a headless launch; 'terminal' is a
   * terminal window, which refuses a chosen account (decision 5).
   */
  forLaunch(o: { named: string | null; projectId: Id | null; machineId: Id; mode: 'headless' | 'terminal' | 'pty'; resume?: boolean; agentKind?: string | null }): {
    account: Omit<LaunchAccountGrant, 'secret'> | null;
    secretId: Id | null;
    notes: string[];
    refused?: string;
  };
  /**
   * What a sign-in terminal signs in on this machine (ADR 0071, amended 20 Oct 2026; ADR 0088
   * amendment): a Claude subscription runs `claude setup-token` in a scratch folder
   * (`setupToken`) and the hub keeps the token, so the account works on every machine; a folder
   * is named only for a folder sign-in. Never the default sign-in. `refused` says why not.
   */
  signInTarget?(id: string, machineId: Id): { accountId: string; name: string; configDir?: string; setupToken?: boolean } | { refused: string; code: 'not-found' | 'bad-request' };
}

// ── Pools (ADR 0088) ────────────────────────────────────────────────────────

/**
 * A launch that names `pool` runs on the account Orgus picks from the agent's pool at launch
 * (choosePoolAccount); a project default may name it too. Never a real account id.
 */
export const POOL_LAUNCH_ACCOUNT_ID = 'pool';
export const POOL_LAUNCH_ACCOUNT_LABEL = 'Choose from the pool';

export type LaunchPoolStrategy = 'in-order' | 'spread';
export const LAUNCH_POOL_STRATEGIES: readonly LaunchPoolStrategy[] = ['in-order', 'spread'];
export const LAUNCH_POOL_STRATEGY_LABEL: Record<LaunchPoolStrategy, string> = { 'in-order': 'In order', spread: 'Spread the load' };
export const LAUNCH_POOL_MAX = 20;

/** One agent's pool: which accounts, in what order, and how Orgus picks between them. */
export interface LaunchPool {
  /** Account ids in the order the pool prefers them (the default sign-in may be one). */
  members: string[];
  /**
   * in-order: the first member that can run now. spread: the member with the most headroom left
   * in its usage windows, then the one launched least recently.
   */
  strategy: LaunchPoolStrategy;
  /** Use the pool for a launch that names no account in a project with no default. */
  whenUnset: boolean;
}
export type LaunchPools = Partial<Record<LaunchAccountAgent, LaunchPool>>;

/** PUT /api/launch-accounts/pools/:agent (every field optional; members [] removes the pool). */
export type LaunchPoolInput = Partial<LaunchPool>;

export type LaunchLimitCause = 'usage-limit' | 'auth';

/**
 * An account the pool skips for a while: a session on it ended on its usage limit (until the
 * reset the error named, else LIMIT_DEFAULT_MS), or signed out (until its folder signs in again,
 * at most LIMIT_AUTH_MS). A person can clear it (DELETE /api/launch-accounts/:id/limit).
 */
export interface LaunchAccountLimit {
  accountId: string;
  /** The machine it was seen on (a sign-out is one folder's); null when it is the account's everywhere. */
  machineId: Id | null;
  cause: LaunchLimitCause;
  /** One plain sentence from the session's failure. */
  summary: string;
  until: Ts;
  at: Ts;
}
/** A usage limit whose reset time the error did not say: Claude's session window. */
export const LIMIT_DEFAULT_MS = 5 * 60 * 60_000;
/** How long a signed-out account stays skipped when no folder report lifts it. */
export const LIMIT_AUTH_MS = 24 * 60 * 60_000;
/** A reset further away than this is not believed (a parse of the wrong number). */
export const LIMIT_MAX_MS = 8 * 24 * 60 * 60_000;

/** The pools as saved, checked. Unknown agents, strategies and ids are dropped. */
export function cleanLaunchPools(raw: unknown): LaunchPools {
  const out: LaunchPools = {};
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out;
  for (const agent of LAUNCH_ACCOUNT_AGENTS) {
    const p = cleanLaunchPool((raw as Record<string, unknown>)[agent], undefined);
    if (p) out[agent] = p;
  }
  return out;
}

/** One pool from input, over what it was (absent fields keep `prev`); null when it has no members. */
export function cleanLaunchPool(raw: unknown, prev: LaunchPool | undefined): LaunchPool | null {
  const r = raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  const okId = (v: unknown): v is string => typeof v === 'string' && /^[\w-]{1,64}$/.test(v) && v !== POOL_LAUNCH_ACCOUNT_ID;
  const members = Array.isArray(r.members) ? [...new Set(r.members.filter(okId))].slice(0, LAUNCH_POOL_MAX) : (prev?.members ?? []);
  if (!members.length) return null;
  const strategy = LAUNCH_POOL_STRATEGIES.includes(r.strategy as LaunchPoolStrategy) ? (r.strategy as LaunchPoolStrategy) : (prev?.strategy ?? 'in-order');
  const whenUnset = typeof r.whenUnset === 'boolean' ? r.whenUnset : (prev?.whenUnset ?? true);
  return { members, strategy, whenUnset };
}

/** One member as the chooser sees it on one machine. */
export interface PoolCandidate {
  id: string;
  name: string;
  /** Why it cannot run now (LaunchAccountsApi.check), or null when it can. */
  blocked: string | null;
  /** An active limit's cause and end, or null. */
  limit: { cause: LaunchLimitCause; until: Ts } | null;
  /** Percent of its tightest usage window still left (0 to 100), null when unknown. */
  headroom: number | null;
  lastLaunchedAt: Ts | null;
}

export interface PoolChoice {
  id: string;
  name: string;
  /** "chosen from the pool: first available; Max B is over its limit until 15:00" */
  why: string;
}

const hhmm = (t: Ts) => {
  const d = new Date(t);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/** Why a member was passed over, in a few words. */
function skippedWords(c: PoolCandidate, now: Ts): string | null {
  if (c.limit && c.limit.until > now) return c.limit.cause === 'auth' ? `${c.name} is signed out` : `${c.name} is over its limit until ${hhmm(c.limit.until)}`;
  if (c.blocked) return `${c.name} cannot run here`;
  return null;
}

/**
 * The account a pool launch runs on (ADR 0088). `candidates` are the pool's members in pool
 * order, as they stand on the launch's machine. A member over its limit or blocked is skipped;
 * the pool picks at launch only, never between a running session's turns.
 */
export function choosePoolAccount(pool: Pick<LaunchPool, 'strategy'>, candidates: PoolCandidate[], now: Ts): PoolChoice | { refused: string } {
  const skipped: string[] = [];
  const open: PoolCandidate[] = [];
  for (const c of candidates) {
    const why = skippedWords(c, now);
    if (why) skipped.push(why);
    else open.push(c);
  }
  if (!open.length) {
    const why = skipped.length ? skipped.join(', ') : 'the pool has no accounts';
    return { refused: `No account in the pool can run now (${why}).` };
  }
  let pick = open[0]!;
  let how = 'first available';
  if (pool.strategy === 'spread') {
    const sorted = [...open].sort((a, b) => (b.headroom ?? 100) - (a.headroom ?? 100) || (a.lastLaunchedAt ?? 0) - (b.lastLaunchedAt ?? 0));
    pick = sorted[0]!;
    how = pick.headroom !== null ? `most headroom, ${Math.round(pick.headroom)}% left` : 'least recently used';
  }
  return { id: pick.id, name: pick.name, why: `chosen from the pool: ${how}${skipped.length ? `; ${skipped.join(', ')}` : ''}` };
}

/** Percent left in the tightest live window of a usage reading, or null when none says. */
export function headroomOf(windows: readonly { percent: number | null; resetsAt: Ts | null }[], now: Ts): number | null {
  let worst: number | null = null;
  for (const w of windows) {
    if (w.percent === null || (w.resetsAt !== null && w.resetsAt <= now)) continue;
    const left = Math.max(0, 100 - w.percent);
    worst = worst === null ? left : Math.min(worst, left);
  }
  return worst;
}

/** The limits still in force now. */
export function liveLimits(limits: readonly LaunchAccountLimit[], now: Ts): LaunchAccountLimit[] {
  return limits.filter((l) => l.until > now);
}

/** The account's active limit on a machine (its own, or an everywhere one), the one ending last. */
export function limitOf(limits: readonly LaunchAccountLimit[], accountId: string, machineId: Id | null, now: Ts): LaunchAccountLimit | null {
  return (
    liveLimits(limits, now)
      .filter((l) => l.accountId === accountId && (l.machineId === null || machineId === null || l.machineId === machineId))
      .sort((a, b) => b.until - a.until)[0] ?? null
  );
}

/** "Over its limit until 15:00", "Signed out": the words a row shows for an active limit. */
export function limitLabel(l: Pick<LaunchAccountLimit, 'cause' | 'until'>): string {
  return l.cause === 'auth' ? 'Signed out' : `Over its limit until ${hhmm(l.until)}`;
}

// ── Testing an account (ADR 0088 decision 10) ───────────────────────────────

/**
 * hub -> agent, with an id: run one tiny Claude Code request on this account now
 * ({ account: LaunchAccountGrant }, the secret leased for this test only). Answered with
 * 'account.tested' (LaunchAccountTestResult). Its rate limit events report the account's usage.
 */
export const LAUNCH_ACCOUNT_TEST_TYPE = 'account.test';
export const LAUNCH_ACCOUNT_TESTED_TYPE = 'account.tested';
/** The request a test sends: one short answer, no tools, no MCP servers, none of the owner's settings. */
export const ACCOUNT_TEST_PROMPT = 'Reply with the single word OK.';
/** How long a test may take before it counts as failed. */
export const ACCOUNT_TEST_TIMEOUT_MS = 120_000;

/** What a machine says about one test. Never a credential. */
export interface LaunchAccountTestResult {
  ok: boolean;
  /** One plain sentence: "Answered in 2.1 s." or what failed and why. */
  summary: string;
  /** The failure's cause (failures.ts), when it failed. */
  cause?: string | null;
  durationMs: number | null;
  /** The model that answered, when Claude Code said. */
  model?: string | null;
}

/** The last test of an account, kept by the hub. */
export interface LaunchAccountTest extends LaunchAccountTestResult {
  at: Ts;
  machineId: Id;
  by: string;
}

// ── Sign-in on a machine from the console (ADR 0108) ────────────────────────

/**
 * Where a console-driven sign-in of a ChatGPT or Google subscription is on one machine:
 *   starting  the agent is starting the vendor's program
 *   open      open `url` and enter `code` there (Codex device code)
 *   paste     open `url`, sign in, and paste the code Google shows back (Gemini CLI)
 *   done      the folder is signed in
 *   failed    `error` says why
 */
export type LaunchSignInStep = 'starting' | 'open' | 'paste' | 'done' | 'failed';
export const LAUNCH_SIGN_IN_STEPS: readonly LaunchSignInStep[] = ['starting', 'open', 'paste', 'done', 'failed'];

/** One sign-in, in state.ext['launch-accounts'].signIns. Kept in memory only, never stored. */
export interface LaunchSignIn {
  accountId: string;
  machineId: Id;
  agent: LaunchAccountAgent;
  step: LaunchSignInStep;
  /** The vendor's sign-in page (https only). */
  url: string | null;
  /** Codex's one-time device code (not a credential: it signs in whoever enters it). */
  code: string | null;
  error: string | null;
  startedAt: Ts;
  updatedAt: Ts;
}

/** Whether an account signs in on each machine from the console (ADR 0108): a ChatGPT or Google subscription. */
export function machineSignInAccount(a: { agent?: LaunchAccountAgent | null; kind: LaunchAccountKind; builtIn?: boolean }): boolean {
  return !a.builtIn && a.kind === 'subscription' && (a.agent === 'codex' || a.agent === 'gemini-cli');
}

/** How long a sign-in may wait for the person before the machine ends it. */
export const LAUNCH_SIGN_IN_TIMEOUT_MS = 10 * 60_000;

/** hub -> agent: start the sign-in: { accountId, agent }. */
export const LAUNCH_SIGN_IN_START_TYPE = 'signin.start';
/** hub -> agent: the code the person pasted: { accountId, code }. Travels once, never kept. */
export const LAUNCH_SIGN_IN_INPUT_TYPE = 'signin.input';
/** hub -> agent: stop it: { accountId }. */
export const LAUNCH_SIGN_IN_CANCEL_TYPE = 'signin.cancel';
/** agent -> hub: { accountId, path, step, url?, code?, error? }. */
export const LAUNCH_SIGN_IN_STATUS_TYPE = 'signin.status';
export interface LaunchSignInStatusMessage {
  accountId: string;
  path?: string;
  step: LaunchSignInStep;
  url?: string | null;
  code?: string | null;
  error?: string | null;
}

/** POST /api/launch-accounts/:id/sign-in/:machineId/code body. */
export interface LaunchSignInCodeInput {
  code: string;
}

/** The longest code a person may paste back. */
export const LAUNCH_SIGN_IN_CODE_MAX = 512;

/** A pasted code as the hub forwards it: trimmed, one line, no spaces; null when it is not one. */
export function cleanSignInCode(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const s = v.trim();
  if (!s || s.length > LAUNCH_SIGN_IN_CODE_MAX || /[\u0000-\u001f\u007f\s]/.test(s)) return null;
  return s;
}

/** An agent's sign-in status, checked: an https URL, a short code, a one-line error. Null when malformed. */
export function cleanSignInStatus(raw: unknown): LaunchSignInStatusMessage | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.accountId !== 'string' || !/^[\w-]{1,64}$/.test(r.accountId)) return null;
  if (!LAUNCH_SIGN_IN_STEPS.includes(r.step as LaunchSignInStep)) return null;
  const url = typeof r.url === 'string' && /^https:\/\/[^\s"<>]{1,2000}$/.test(r.url) ? r.url : null;
  const code = typeof r.code === 'string' && /^[A-Za-z0-9-]{4,24}$/.test(r.code) ? r.code : null;
  const error = typeof r.error === 'string' && r.error.trim() ? r.error.replace(/\s+/g, ' ').trim().slice(0, 300) : null;
  const path = isLaunchFolderPath(r.path) ? r.path : undefined;
  return { accountId: r.accountId, step: r.step as LaunchSignInStep, url, code, error, ...(path ? { path } : {}) };
}
